import { ChangeEvent, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import $api from "@/zapi-frontend/shared/api/axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getRouteProjects, getRouteSignIn } from "@/zapi-frontend/shared/const/router";
import { LogoSvg } from "@libs/zapi-ui/assets";
import { AppButton, AppInput, FigmaTypography } from "@libs/zapi-ui/custom";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";

export function VerifyOtpPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const { data: isAuth, isLoading } = useAuthenticated();
  const [otpCode, setOtpCode] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const twoFactorToken = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (isLoading) return;
    if (isAuth) {
      navigate(getRouteProjects());
      return;
    }
    if (!twoFactorToken) navigate(getRouteSignIn());
  }, [twoFactorToken, navigate, isAuth, isLoading]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const payload = { token: twoFactorToken, otpCode };
      return await $api.post("/auth/verify-otp", payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["session-verify"] });
      const next = new URLSearchParams(search).get("next");
      if (next) {
        navigate(next);
        return;
      }
      navigate(getRouteProjects());
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to verify code";
      toast.error(message);
    }
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      setIsResending(true);
      const payload = { token: twoFactorToken };
      return await $api.post("/auth/resend-otp", payload);
    },
    onSuccess: () => {
      toast.success("Verification code resent to your email");
      setCountdown(60);
      setIsResending(false);
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to resend code";
      toast.error(message);
      setIsResending(false);
      if (err?.response?.status === 401) {
        navigate(getRouteSignIn());
      }
    }
  });

  function onChangeOtp(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
  }

  function verifyOtp() {
    if (otpCode.length !== 6) return;

    verifyMutation.mutate();
  }

  function resendOtp() {
    if (countdown > 0 || isResending) return;

    resendMutation.mutate();
  }

  const isValidOtp = otpCode.length === 6;

  return (
    <div className="flex flex-col items-center h-[calc(100vh-40px)]">
      <LogoSvg className="my-[64px]" />
      <div className="max-w-[400px] w-full">
        <div>
          <div className="flex mb-[32px] flex-col items-center">
            <FigmaTypography component="h5" variant="heading-1">Enter Verification Code</FigmaTypography>
            <p className="text-innoveraGrayscale-500 mt-4 text-center">
              We sent a 6-digit verification code to your email. Please enter it below.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verifyOtp();
            }}
          >
            <div className="flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[4px]">
                <label className="text-inLabel font-medium mb-[4px]">Verification Code</label>
                <AppInput
                  value={otpCode}
                  onChange={onChangeOtp}
                  placeholder="000000"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  required
                />
              </div>
              <div className="flex justify-center">
                <AppButton
                  appearance="text"
                  className="w-full"
                  type="button"
                  onClick={resendOtp}
                  disabled={countdown > 0 || isResending}
                >
                  {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
                </AppButton>
              </div>
            </div>
            <AppButton
              color="orange400"
              className="w-full mt-2"
              disabled={!isValidOtp || verifyMutation.isPending}
              type="submit"
            >
              {verifyMutation.isPending ? "Verifying..." : "Verify"}
            </AppButton>
          </form>
        </div>
      </div>
    </div>
  );
}
